import { getPublicVoiceTonePageApi } from "../../service/voicePublic";

const MAX_FILTER_PERMUTATIONS = 24;

/** 未选任何项：请求里不传该维度；选多项：拆成多请求时在对应分支上传具体值 */
export function expandDimForRequest(selected: string[]): (string | undefined)[] {
    if (selected.length === 0) return [undefined];
    return [...selected];
}

export function cartesian4(
    L: (string | undefined)[],
    G: (string | undefined)[],
    A: (string | undefined)[],
    T: (string | undefined)[]
): [string | undefined, string | undefined, string | undefined, string | undefined][] {
    const out: [string | undefined, string | undefined, string | undefined, string | undefined][] = [];
    for (const l of L) {
        for (const g of G) {
            for (const a of A) {
                for (const t of T) {
                    out.push([l, g, a, t]);
                }
            }
        }
    }
    return out;
}

function product(L: (string | undefined)[], G: (string | undefined)[], A: (string | undefined)[], T: (string | undefined)[]) {
    return L.length * G.length * A.length * T.length;
}

/** 组合数过大时，逐个把「多值」维度降级为不带到请求里，由前端再筛 */
export function softenDims(
    L: (string | undefined)[],
    G: (string | undefined)[],
    A: (string | undefined)[],
    T: (string | undefined)[]
): [(string | undefined)[], (string | undefined)[], (string | undefined)[], (string | undefined)[]] {
    let Ll = [...L];
    let Gg = [...G];
    let Aa = [...A];
    let Tt = [...T];

    while (product(Ll, Gg, Aa, Tt) > MAX_FILTER_PERMUTATIONS) {
        const candidates: { len: number; key: "L" | "G" | "A" | "T" }[] = [];
        if (Ll.length > 1) candidates.push({ len: Ll.length, key: "L" });
        if (Gg.length > 1) candidates.push({ len: Gg.length, key: "G" });
        if (Aa.length > 1) candidates.push({ len: Aa.length, key: "A" });
        if (Tt.length > 1) candidates.push({ len: Tt.length, key: "T" });
        if (!candidates.length) break;
        candidates.sort((a, b) => b.len - a.len);
        const k = candidates[0].key;
        if (k === "L") Ll = [undefined];
        else if (k === "G") Gg = [undefined];
        else if (k === "A") Aa = [undefined];
        else Tt = [undefined];
    }
    return [Ll, Gg, Aa, Tt];
}

export function mergeVoiceListsUnique(lists: VoicePublic.VoiceToneItem[][]): VoicePublic.VoiceToneItem[] {
    const map = new Map<number, VoicePublic.VoiceToneItem>();
    for (const list of lists) {
        for (const item of list) {
            map.set(item.id, item);
        }
    }
    return [...map.values()];
}

export type FetchPublicVoiceListMultiOpts = {
    /**
     * true：仅请求已发布（公共音色库默认）
     * false：请求不带 published 且不做可用性过滤，供管理员列表拉全量
     */
    restrictToPublished?: boolean;
};

function asFlag(value: unknown): boolean {
    return value === true || value === 1 || value === "true" || value === "1";
}

export function normalizeVoiceCloneFlags(item: VoicePublic.VoiceToneItem): VoicePublic.VoiceToneItem {
    return {
        ...item,
        cloneFinished: asFlag(item.cloneFinished),
        cloneFailed: asFlag(item.cloneFailed),
    };
}

export function isVoiceCloneSuccessful(item: VoicePublic.VoiceToneItem): boolean {
    return item.cloneFinished === true && item.cloneFailed === false;
}

export function isPublicVoiceAvailable(item: VoicePublic.VoiceToneItem): boolean {
    return item.published === true && isVoiceCloneSuccessful(item);
}

export function isMineVoiceAvailable(item: VoicePublic.VoiceToneItem): boolean {
    return isVoiceCloneSuccessful(item);
}

/** 各维度内为 OR，维度之间为 AND */
export function itemMatchesMultiFilters(
    item: VoicePublic.VoiceToneItem,
    langs: string[],
    genders: string[],
    ages: string[],
    timbres: string[]
): boolean {
    if (langs.length && !langs.includes(item.langCode)) return false;
    if (genders.length && !genders.includes(item.genderValue)) return false;
    if (ages.length && !ages.includes(item.ageValue)) return false;
    if (timbres.length && !timbres.includes(item.timbreValue)) return false;
    return true;
}

export async function fetchPublicVoiceListMulti(
    debouncedVoiceName: string,
    langs: string[],
    genders: string[],
    ages: string[],
    timbres: string[],
    opts?: FetchPublicVoiceListMultiOpts
): Promise<VoicePublic.VoiceToneItem[]> {
    const base: VoicePublic.VoiceTonePageParams = {
        pageNo: 1,
        pageSize: 100,
    };
    if (opts?.restrictToPublished === false) {
        // 不传 published
    } else {
        base.published = true;
    }
    if (debouncedVoiceName) base.voiceName = debouncedVoiceName;

    let L = expandDimForRequest(langs);
    let G = expandDimForRequest(genders);
    let A = expandDimForRequest(ages);
    let T = expandDimForRequest(timbres);
    [L, G, A, T] = softenDims(L, G, A, T);

    const combos = cartesian4(L, G, A, T);
    const requests: VoicePublic.VoiceTonePageParams[] = combos.map(([l, g, a, t]) => {
        const p: VoicePublic.VoiceTonePageParams = { ...base };
        if (l !== undefined) p.langCode = l;
        if (g !== undefined) p.genderValue = g;
        if (a !== undefined) p.ageValue = a;
        if (t !== undefined) p.timbreValue = t;
        return p;
    });

    const responses = await Promise.all(requests.map((p) => getPublicVoiceTonePageApi(p)));
    const lists: VoicePublic.VoiceToneItem[][] = [];
    for (const res of responses) {
        if (res.code === 0 && res.data && Array.isArray(res.data.list)) {
            lists.push(res.data.list);
        }
    }

    let merged = mergeVoiceListsUnique(lists).map(normalizeVoiceCloneFlags);
    merged = merged.filter((item) => itemMatchesMultiFilters(item, langs, genders, ages, timbres));
    if (opts?.restrictToPublished !== false) {
        merged = merged.filter(isPublicVoiceAvailable);
    }
    return merged;
}
